import { CommonModule } from "@angular/common";
import { Component, inject, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Invitation } from "@models/invitation";
import { InviteGuestDTO } from "@models/invite-guest";
import { CourtesyTicketOption } from "@models/courtesy-ticket";
import { splitPhoneNumber } from "@shared/helpers/phoneNumber";
import { phoneNumberValidator } from "@shared/validatos";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { Subject, finalize, take, takeUntil } from "rxjs";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DdiSelectComponent } from "../../../../../shared/components/ddi-select/ddi-select.component";
import { DialogHeaderComponent } from "../../../../../shared/components/dialog-header/dialog-header.component";
import { EventsManagementService } from "../../../../services/events-management.service";
import { InvitationsService } from "../../../../services/invitations.service";

export interface AddGuestDialogData {
  eventId: number;
  participant?: Invitation;
}

@Component({
  selector: "app-add-guest-dialog",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    DdiSelectComponent,
    MatDialogModule,
    DialogHeaderComponent,
    ButtonComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: "./add-guest-dialog.component.html",
  styleUrl: "./add-guest-dialog.component.scss",
})
export class AddGuestDialogComponent implements OnInit, OnDestroy {
  private readonly _fb = inject(FormBuilder);
  private readonly _eventService = inject(EventsManagementService);
  private readonly _invitationsService = inject(InvitationsService);
  readonly dialogRef = inject(MatDialogRef<AddGuestDialogComponent>);
  private readonly destroy$ = new Subject<void>();

  eventId: number;
  participant?: Invitation;

  loading = false;
  loadingTickets = false;
  tickets: CourtesyTicketOption[] = [];
  errorMessage = "";

  constructor(@Inject(MAT_DIALOG_DATA) public data: AddGuestDialogData) {
    this.eventId = data.eventId;
    this.participant = data.participant;
  }

  form = this._fb.group({
    user_email: ["", [Validators.required, Validators.email]],
    user_phone_number: ["", [Validators.required, phoneNumberValidator]],
    ddi: ["+55", Validators.required],
    description: [""],
    ticket_id: this._fb.control<number | null>(null, Validators.required),
    quantity: [1, [Validators.required, Validators.min(1), Validators.pattern(/^[0-9]+$/)]],
    transferable: [false],
  });

  controls = this.form.controls;

  ngOnInit(): void {
    if (this.participant) {
      this.populateFormWithGuestData();
      this.controls.ticket_id.clearValidators();
      this.controls.ticket_id.disable();
      this.controls.quantity.disable();
    } else {
      this.loadTickets();
    }
    this.subscribeToQuantity();
    this.controls.ticket_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validateStock());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedTicket(): CourtesyTicketOption | undefined {
    return this.tickets.find((ticket) => ticket.id === Number(this.controls.ticket_id.value));
  }

  loadTickets() {
    this.loadingTickets = true;
    this._eventService.getCourtesyTickets(this.eventId)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => { this.loadingTickets = false; }))
      .subscribe({
        next: ({ tickets }) => { this.tickets = tickets; this.validateStock(); },
        error: () => { this.errorMessage = "Não foi possível carregar os ingressos. Tente novamente."; },
      });
  }

  private validateStock() {
    const validators = [Validators.required, Validators.min(1), Validators.pattern(/^[0-9]+$/)];
    if (this.selectedTicket) validators.push(Validators.max(this.selectedTicket.available_quantity));
    this.controls.quantity.setValidators(validators);
    this.controls.quantity.updateValueAndValidity({ emitEvent: false });
  }

  confirm() {
    if (this.loading || this.loadingTickets) return;
    this.validateStock();
    this.form.markAllAsTouched();
    if (this.form.invalid || (!this.participant && !this.selectedTicket)) return;

    const raw = this.form.getRawValue();
    const guest = {
      ...raw,
      ticket_id: raw.ticket_id == null ? undefined : Number(raw.ticket_id),
      user_email: raw.user_email?.trim().toLowerCase(),
      user_phone_number: `${raw.ddi}${raw.user_phone_number}`,
    } as InviteGuestDTO;
    this.loading = true;
    this.errorMessage = "";
    const request = this.participant
      ? this._invitationsService.updateInvitations(this.participant.id, guest)
      : this._eventService.sendGuestInvite(this.eventId, guest);
    request.pipe(take(1), takeUntil(this.destroy$), finalize(() => { this.loading = false; }))
      .subscribe({
        next: (res) => { if (res) this.dialogRef.close(true); },
        error: (err) => {
          this.errorMessage = err.error?.error || "Não foi possível salvar a cortesia. Tente novamente.";
          if (!this.participant && err.status === 422) this.loadTickets();
        },
      });
  }

  private populateFormWithGuestData() {
    if (!this.participant) {
      return;
    }
    const { ddi, nationalNumber } = splitPhoneNumber(
      this.participant.user_phone_number,
    );

    this.form.patchValue({
      ...this.participant,
      ddi: ddi,
      user_phone_number: nationalNumber,
    });
  }

  private subscribeToQuantity() {
    this.form.controls.quantity.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value) return;
        if (value > 1) {
          this.form.controls.transferable.setValue(true);
        } else {
          this.form.controls.transferable.setValue(false);
        }
      });
  }
}
